import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from './client';

// ============================================================================
// Types
// ============================================================================

export interface EmailChannelInfo {
  id: string;
  email: string;
  name: string;
  isActive: boolean;
}

export interface TelegramChannelInfo {
  configured: boolean;
  botUsername: string | null;
  activeChatCount: number;
}

export interface WidgetChannelInfo {
  configured: boolean;
  widgetKey: string | null;
  name: string | null;
  enabled: boolean;
}

export interface ChannelsStatus {
  email: EmailChannelInfo[];
  telegram: TelegramChannelInfo;
  widget: WidgetChannelInfo;
}

export interface WidgetConfig {
  id: string;
  widgetKey: string;
  name: string;
  greeting: string | null;
  enabled: boolean;
  color: string;
  subtitle: string | null;
  placeholder: string | null;
  position: 'bottom-right' | 'bottom-left';
  branding: boolean;
  botName: string | null;
  botAvatarUrl: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

export function useChannelsStatus() {
  return useQuery({
    queryKey: ['channels', 'status'],
    queryFn: () =>
      client.get<ChannelsStatus>('/channels/status').then((r) => r.data),
  });
}

export function useWidgetConfig() {
  return useQuery({
    queryKey: ['widget', 'config'],
    queryFn: () =>
      client.get<{ config: WidgetConfig | null }>('/widget/config').then((r) => r.data.config),
  });
}

export interface WidgetConfigUpdate {
  name?: string;
  greeting?: string;
  enabled?: boolean;
  color?: string;
  subtitle?: string;
  placeholder?: string;
  position?: 'bottom-right' | 'bottom-left';
  branding?: boolean;
  botName?: string;
  botAvatarUrl?: string;
}

export function useCreateWidgetConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { name?: string; greeting?: string }) =>
      client.post<{ config: { id: string; widgetKey: string } }>('/widget/config', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['widget', 'config'] });
      void queryClient.invalidateQueries({ queryKey: ['channels', 'status'] });
    },
  });
}

export function useUpdateWidgetConfig() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: WidgetConfigUpdate) =>
      client.post<{ config: { id: string; widgetKey: string } }>('/widget/config', data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['widget', 'config'] });
      void queryClient.invalidateQueries({ queryKey: ['channels', 'status'] });
    },
  });
}
